import { TmsData, TrackingData } from '@ecm/router';

import { UnitEnum } from '../../../api/dashboard/item/itemSection/unit.enum';
import { BrandEnum } from '../../../api/dashboardV7/brandEnum';
import { ContractV7LastBill } from '../../../api/dashboardV7/contract/contractV7LastBill';
import { ContractV7 } from '../../../api/dashboardV7/contractV7';
import { DashboardV7 } from '../../../api/dashboardV7/dashboardV7';
import { IdentityTypeEnum } from '../../../api/dashboardV7/identity-type.enum';
import { Option } from '../../../api/dashboardV7/option';
import { PushV7 } from '../../../api/dashboardV7/pushV7';
import { ButtonViewModel } from '../../../model/home/button.view-model';
import { CommercialPushViewModel } from '../../../model/home/commercialPush.view-model';
import { DashboardV7ViewModel } from '../../../model/home/dashboard/dashboardV7.view-model';
import { ItemHelpViewModel } from '../../../model/home/itemHelp.view-model';
import { ItemHomeViewModel } from '../../../model/home/itemHome.view-model';
import { ItemV7ViewModel } from '../../../model/home/itemV7.view-model';
import { LasBillViewModel } from '../../../model/home/lastBill.view-model';
import { OptionsViewModel } from '../../../model/home/options.view-model';
import { PushV7ViewModel } from '../../../model/home/pushV7.view-model';
import { SectionV7ViewModel } from '../../../model/home/sectionV7.view-model';
import { KenobiCodes } from '../../../model/tracking/kenobiCodes';
import { TmsCodes } from '../../../model/tracking/tmsCodes.enum';
import { TrackedPage } from '../../../model/tracking/trackedPage.enum';
import { DeviceSimMapper } from '../device-sim/device-sim.mapper';
import { EntertainmentMapper } from '../entertainment/entertainment.mapper';
import { ForbiddenDataMapper } from '../forbidden/forbidden-data.mapper';
import { DashboardV7MapperUtils } from './dashboardV7-utils.mapper';

export class DashboardV7ViewModelMapper {
    private static readonly pageTitle: string = 'accueil_espace_client_logue';

    public static getDashboardViewModel(dashboard: DashboardV7, dashboardTranslations: any): DashboardV7ViewModel {
        const model: DashboardV7ViewModel = new DashboardV7ViewModel();
        if (dashboard.universe) {
            model.universe = dashboard.universe;
        }
        if (dashboard.sharedServices) {
            model.sharedServices = dashboard?.sharedServices;
        }
        if (dashboard.isEligibleToBloom) {
            model.isEligibleToBloom = dashboard.isEligibleToBloom;
        }

        //Calcul du NSRU
        if (dashboard.contracts && dashboard.contracts.length > 0) {
            model.isSharedServicesNsru = dashboard.contracts[0].identityType === IdentityTypeEnum.NSRU;
        } else {
            model.isSharedServicesNsru = true;
        }

        if (
            dashboard.forbidden &&
            ['MULTIBAL', 'PARNASSE', 'OBS', 'PRO'].indexOf(dashboard.forbidden.clientType) !== -1
        ) {
            model.forbidden = ForbiddenDataMapper.extractForbiddenContract(dashboard.forbidden);
            model.trackingLabel = model.forbidden.trackingLabel;
            model.kenobiCode = KenobiCodes.ACCNCONS;
        } else {
            if (dashboard.pushes && dashboard.pushes.length > 0) {
                this.extractFirstSection(model, dashboard.pushes);
            }
            if (dashboard.contracts && dashboard.contracts.length > 0) {
                this.extractSection(model, dashboard.contracts, dashboardTranslations);
            }
            model.kenobiCode = KenobiCodes.ACCMS;
            model.trackingLabel = this.pageTitle;
        }

        return model;
    }

    private static extractSection(
        model: DashboardV7ViewModel,
        contracts: ContractV7[],
        dashboardTranslations: any,
    ): void {
        contracts.forEach((contract) => {
            const sectionV7ViewModel: SectionV7ViewModel = new SectionV7ViewModel();
            const HomeViewModel: ItemHomeViewModel = new ItemHomeViewModel();

            this.extractContractBasicInfo(sectionV7ViewModel, contract);
            this.extractContractOffer(sectionV7ViewModel, contract);

            if (contract.lastBill != null) {
                sectionV7ViewModel.lastBill = this.extractLastBill(contract.lastBill, contract);
            }

            if (contract.pushes.length > 0) {
                this.extractContractPush(sectionV7ViewModel, contract.pushes, model, contract);
            }

            if (contract.devices) {
                this.processDevices(sectionV7ViewModel, HomeViewModel, contract, dashboardTranslations);
            }

            if (contract.options) {
                this.processOptions(sectionV7ViewModel, contract);
            }

            if (sectionV7ViewModel.itemsSim.length > 0) {
                sectionV7ViewModel.itemsSim[0].isMultiSim = true;
            }
            // Remove onEntertainmentPush push contract if no entertainment section displayed
            this.onEntertainmentPush(sectionV7ViewModel);
            model.sections.push(sectionV7ViewModel);
        });
    }

    private static extractContractBasicInfo(sectionV7ViewModel: SectionV7ViewModel, contract: ContractV7): void {
        sectionV7ViewModel.contractId = contract.cid;
        sectionV7ViewModel.brand = contract.brand;

        if (contract.message) {
            sectionV7ViewModel.message = contract.message;
        }
    }

    private static extractContractOffer(sectionV7ViewModel: SectionV7ViewModel, contract: ContractV7): void {
        if (!contract.offer) return;

        sectionV7ViewModel.offer = contract.offer;
        sectionV7ViewModel.offer.trackingData = Object.assign(new TrackingData(), {
            tmsTracking: {
                trackedPage: TrackedPage.dashboard,
                trackingArea: 'care_hp_entrees',
                trackingName: 'offre_et_options',
                additionalProperties: this.buildTrackingAdditionalProperties(contract),
            },
        });

        if (contract.referenceId) {
            sectionV7ViewModel.offer.referenceId = contract.referenceId;
        }

        if (contract.devices.length > 0) {
            const mobileContracts = contract.devices.find(
                (device) => device.category === 'sim' && device.section === 'Mobile',
            );
            sectionV7ViewModel.offer.mobileNumber = mobileContracts ? mobileContracts.id : null;
        }
    }

    private static processDevices(
        sectionV7ViewModel: SectionV7ViewModel,
        HomeViewModel: ItemHomeViewModel,
        contract: ContractV7,
        dashboardTranslations: any,
    ): void {
        contract.devices.forEach((device) => {
            if (device.category == 'sim') {
                this.processSimDevice(sectionV7ViewModel, device, contract, dashboardTranslations);
            } else if (device.category == 'multisim') {
                this.processMultiSimDevice(sectionV7ViewModel, device, contract, dashboardTranslations);
            } else if (device.category == 'home') {
                this.processHomeDevice(sectionV7ViewModel, HomeViewModel, device, contract, dashboardTranslations);
            } else {
                this.processOtherDevice(sectionV7ViewModel, device, contract, dashboardTranslations);
            }
        });
    }

    private static processSimDevice(
        sectionV7ViewModel: SectionV7ViewModel,
        device: any,
        contract: ContractV7,
        dashboardTranslations: any,
    ): void {
        const itemSimViewModel = this.extractItemSim(
            device,
            contract.brand,
            contract.cid,
            dashboardTranslations,
            contract,
        );
        if (itemSimViewModel) {
            sectionV7ViewModel.itemsSim.push(itemSimViewModel);
        }
    }

    private static processMultiSimDevice(
        sectionV7ViewModel: SectionV7ViewModel,
        device: any,
        contract: ContractV7,
        dashboardTranslations: any,
    ): void {
        const itemMultiSimViewModel = this.extractItemSim(
            device,
            contract.brand,
            contract.cid,
            dashboardTranslations,
            contract,
        );
        if (itemMultiSimViewModel) {
            itemMultiSimViewModel.trackingData = Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_hp_mobile',
                    trackingName: this.formatTextTracking(itemMultiSimViewModel.name),
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            });
            sectionV7ViewModel.itemMultiSim.push(itemMultiSimViewModel);
        }
    }

    private static processHomeDevice(
        sectionV7ViewModel: SectionV7ViewModel,
        HomeViewModel: ItemHomeViewModel,
        device: any,
        contract: ContractV7,
        dashboardTranslations: any,
    ): void {
        if (device.help) {
            sectionV7ViewModel.itemHelp = this.extractHomeHelp(device, contract.cid, contract);
        }

        const itemHomeViewModel = this.extractItemHome(
            device,
            contract.brand,
            contract.cid,
            dashboardTranslations,
            contract,
        );
        if (itemHomeViewModel) {
            if (itemHomeViewModel.id && itemHomeViewModel.name == 'fixe') {
                HomeViewModel.id = itemHomeViewModel.id;
                HomeViewModel.section = itemHomeViewModel.section;
            }
            HomeViewModel.items.push(itemHomeViewModel);
            sectionV7ViewModel.itemsHome = HomeViewModel;
        }
    }

    private static processOtherDevice(
        sectionV7ViewModel: SectionV7ViewModel,
        device: any,
        contract: ContractV7,
        dashboardTranslations: any,
    ): void {
        const itemEntertainmentViewModel = this.extractItemEntertainment(
            device,
            contract.brand,
            dashboardTranslations,
            contract,
        );
        if (itemEntertainmentViewModel) {
            sectionV7ViewModel.itemsEntertainment.push(itemEntertainmentViewModel);
        }
    }

    private static processOptions(sectionV7ViewModel: SectionV7ViewModel, contract: ContractV7): void {
        const itemOptionsViewModel = this.extractOptions(contract.options, contract);
        sectionV7ViewModel.options = itemOptionsViewModel;

        this.processEntertainmentOptions(sectionV7ViewModel, itemOptionsViewModel, contract);
        this.processCybersecureOptions(sectionV7ViewModel, itemOptionsViewModel, contract);
    }

    private static processEntertainmentOptions(
        sectionV7ViewModel: SectionV7ViewModel,
        itemOptionsViewModel: OptionsViewModel,
        contract: ContractV7,
    ): void {
        const hasImage = itemOptionsViewModel?.optionsTv.some(
            (option) => option.imageUrl && option.category === 'entertainment',
        );

        if (
            hasImage &&
            itemOptionsViewModel.optionsTv.length > 0 &&
            sectionV7ViewModel.itemsEntertainment.length == 0
        ) {
            const itemEntertainmentViewModel = Object.assign(new ItemV7ViewModel(), {
                section: 'TV et divertissement',
                name: itemOptionsViewModel.optionsTv[0].name,
                category: itemOptionsViewModel.optionsTv[0].category,
                link: itemOptionsViewModel.optionsTv[0].link,
                imageUrl: itemOptionsViewModel.optionsTv[0].imageUrl,
                content: itemOptionsViewModel.optionsTv[0].description,
                brand: contract.brand,
                isTv: true,
                trackingData: Object.assign(new TrackingData(), {
                    tmsTracking: {
                        trackedPage: TrackedPage.dashboard,
                        trackingArea: 'care_tvdivertissement',
                        trackingName: 'votre_tv_et_divertissement',
                        additionalProperties: this.buildTrackingAdditionalProperties(contract),
                    },
                }),
            });
            itemOptionsViewModel.optionsTv.shift();
            sectionV7ViewModel.options = itemOptionsViewModel;
            sectionV7ViewModel.itemsEntertainment.push(itemEntertainmentViewModel);
        }
    }

    private static processCybersecureOptions(
        sectionV7ViewModel: SectionV7ViewModel,
        itemOptionsViewModel: OptionsViewModel,
        contract?: ContractV7,
    ): void {
        if (itemOptionsViewModel.optionsCybersecure.length > 0) {
            const tmsData = new TmsData();
            tmsData.trackedPage = TrackedPage.dashboard;
            tmsData.trackingArea = 'care_hp_cybersecurite';
            tmsData.trackingName = 'orange_cybersecure';
            tmsData.additionalProperties = this.buildTrackingAdditionalProperties(contract);
            const itemCybersecureViewModel = Object.assign(new ItemV7ViewModel(), {
                section: 'Cybersécurité',
                name: itemOptionsViewModel.optionsCybersecure[0].name,
                category: itemOptionsViewModel.optionsCybersecure[0].section,
                link: itemOptionsViewModel.optionsCybersecure[0].link,
                imageUrl: itemOptionsViewModel.optionsCybersecure[0].imageUrl,
                content: itemOptionsViewModel.optionsCybersecure[0].description,
                isCybersecure: true,
                trackingData: Object.assign(new TrackingData(), {
                    tmsTracking: tmsData,
                }),
            });
            itemOptionsViewModel.optionsCybersecure.shift();
            sectionV7ViewModel.options = itemOptionsViewModel;
            sectionV7ViewModel.itemsCybersecure.push(itemCybersecureViewModel);
        }
    }

    private static onEntertainmentPush(sectionV7ViewModel: SectionV7ViewModel): void {
        if (
            sectionV7ViewModel.itemsEntertainment.length === 0 &&
            sectionV7ViewModel.commercialPush?.entertainmentPush
        ) {
            sectionV7ViewModel.commercialPush.entertainmentPush = null;
        }
    }

    private static extractItemSim(
        item: any,
        brand: BrandEnum,
        cid: string,
        dashboardTranslations: any,
        contract?: ContractV7,
    ): ItemV7ViewModel | null {
        return Object.assign(new ItemV7ViewModel(), {
            id: item.id,
            section: item.section,
            name: item.name,
            category: item.category,
            content: this.formatContent(item.content),
            link: item.link,
            imageUrl: item.imageUrl,
            subItems: DeviceSimMapper.extractSubItemSim(item, cid, dashboardTranslations),
            suspendedInfo: DashboardV7MapperUtils.extractSuspendedInfo(item, TmsCodes.care_hp_mobile),
            partialWarnTitle: DashboardV7MapperUtils.extractWarnTitle(item),
            picto: item.picto,
            brand: brand,
            trackingData: Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_hp_mobile',
                    trackingName: 'votre_' + this.formatTextTracking(item.section),
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            }),
        });
    }

    private static extractItemHome(
        item: any,
        brand: BrandEnum,
        cid: string,
        dashboardTranslations: any,
        contract?: ContractV7,
    ): ItemV7ViewModel | null {
        const checkContent = this.checkcontent(item.content);
        let lastContent: string;
        if (checkContent) {
            lastContent = item.content.splice(-1);
        }
        const res = Object.assign(new ItemV7ViewModel(), {
            contractId: cid,
            id: item.id,
            section: item.section,
            name: item.name,
            category: item.category,
            content: this.formatContent(item.content),
            link: item.link,
            imageUrl: item.imageUrl,
            subItems: DeviceSimMapper.extractSubItemFixe(item, cid, dashboardTranslations),
            suspendedInfo: DashboardV7MapperUtils.extractSuspendedInfo(item, TmsCodes.care_hp_mobile),
            partialWarnTitle: DashboardV7MapperUtils.extractWarnTitle(item),
            picto: item.picto,
            brand: brand,
            trackingData: Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_hp_maison',
                    trackingName: 'votre_' + this.formatTextTracking(item.name),
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            }),
            actions: item.actions,
            actionsKo: item.actionsKo,
        });
        if (checkContent) {
            res.contentFixe = Array.isArray(lastContent) ? lastContent[0] : lastContent;
        }
        return res;
    }

    private static extractItemEntertainment(
        item: any,
        brand: BrandEnum,
        dashboardTranslations: any,
        contract?: ContractV7,
    ): ItemV7ViewModel | null {
        return Object.assign(new ItemV7ViewModel(), {
            id: item.id,
            section: item.section,
            name: item.name,
            category: item.category,
            content: this.formatContent(item.content),
            link: item.link,
            imageUrl: item.imageUrl,
            subItems: EntertainmentMapper.extractSubItemEntertainment(item, dashboardTranslations),
            button: DashboardV7MapperUtils.extractButton(item, TmsCodes.care_hp_tvdivertissement),
            partialWarnTitle: DashboardV7MapperUtils.extractWarnTitle(item),
            picto: item.picto,
            brand: brand,
            isTv: false,
            trackingData: Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_tvdivertissement',
                    trackingName: 'votre_' + this.formatTextTracking(item.section),
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            }),
            actions: item.actions,
        });
    }

    private static extractHomeHelp(item: any, cid?: string, contract?: ContractV7): ItemHelpViewModel | null {
        return Object.assign(new ItemHelpViewModel(), {
            cid: cid,
            text: item.help.text,
            link: item.help.link,
            trackingData: Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_hp_maison',
                    trackingName: 'tester_et_depanner',
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            }),
        });
    }

    private static extractLastBill(lastBill: ContractV7LastBill, contract?: ContractV7): LasBillViewModel {
        return Object.assign(new LasBillViewModel(), {
            date: lastBill?.date,
            amount: DashboardV7MapperUtils.convertMainCredit(lastBill?.amount, UnitEnum.CURRENCY),
            isUnpaid: lastBill?.isUnpaid,
            link: lastBill?.link,
            trackingData: Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_hp_entrees',
                    trackingName: 'factures',
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            }),
        });
    }

    private static extractOptions(options: Option[], contract?: ContractV7): OptionsViewModel | null {
        const optionsViewModel = new OptionsViewModel();
        options.forEach((element) => {
            if (element.category == 'entertainment') {
                optionsViewModel.optionsTv.push(element);
            }
            if (element.category == 'sim') {
                optionsViewModel.optionsMobile.push(element);
            }
            if (element.section == 'Cybersecure') {
                optionsViewModel.optionsCybersecure.push(element);
            }
        });

        optionsViewModel.optionsTv.forEach((element) => {
            element.trackingData = Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_tvdivertissement',
                    trackingName: this.formatTextTracking(element.name),
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            });
        });
        optionsViewModel.optionsMobile.forEach((element) => {
            element.trackingData = Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_hp_mobile',
                    trackingName: element.name,
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            });
        });
        optionsViewModel.optionsCybersecure.forEach((element) => {
            element.trackingData = Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_hp_cybersecurite',
                    trackingName: element.name,
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            });
        });
        return optionsViewModel;
    }

    private static extractFirstSection(model: DashboardV7ViewModel, pushes: PushV7[]): void {
        pushes.forEach((element) => {
            if (element.type == 'relational') {
                model.relational = DashboardV7MapperUtils.getRelationalInfo(element);
            }
            if (element.type == 'megapush') {
                model.megapush = DashboardV7MapperUtils.getMegapushPcm(element);
            }
            if (element.type == 'alert') {
                model.alerts = DashboardV7MapperUtils.getAlerts(pushes);
            }
            if (element.type == 'info') {
                model.infos = DashboardV7MapperUtils.getInfos(pushes);
            }
            if (element.type == 'warning') {
                model.warnings = DashboardV7MapperUtils.getWarnings(pushes);
            }
        });
    }

    /**
     * Extract first section for advise push
     * @param {SectionV7ViewModel} model
     * @param {PushV7[]} pushes
     * @param {DashboardV7ViewModel} dashModel
     * @param {ContractV7} contract
     * @private
     */
    private static extractContractPush(
        model: SectionV7ViewModel,
        pushes: PushV7[],
        dashModel?: DashboardV7ViewModel,
        contract?: ContractV7,
    ): void {
        const commercialPushViewModel: CommercialPushViewModel = new CommercialPushViewModel();
        let changerOffrePushViewModel: PushV7ViewModel = new PushV7ViewModel();
        let changerMobilePushViewModel: PushV7ViewModel = new PushV7ViewModel();
        pushes.forEach((element) => {
            if (element.type === 'change-offer') {
                changerOffrePushViewModel = element;
                changerOffrePushViewModel.trackingData = new TrackingData({
                    tmsTracking: {
                        trackedPage: TrackedPage.dashboard,
                        trackingArea: 'care_hp_entrees',
                        trackingName: this.getChangeOfferTrackingName(element),
                        additionalProperties: this.buildTrackingAdditionalProperties(contract),
                    },
                });

                changerOffrePushViewModel.button = new ButtonViewModel(element.code);
                changerOffrePushViewModel.button = DashboardV7MapperUtils.extractButton(
                    element,
                    TmsCodes.care_hp_entrees,
                );
                model.changeOffer = changerOffrePushViewModel;
            }
            if (element.type === 'change-mobile') {
                changerMobilePushViewModel = element;

                changerMobilePushViewModel.trackingData = Object.assign(new TrackingData(), {
                    tmsTracking: {
                        trackedPage: TrackedPage.dashboard,
                        trackingArea: TmsCodes.care_hp_mobile,
                        trackingName: 'changer_de_mobile',
                        additionalProperties: this.buildTrackingAdditionalProperties(contract),
                    },
                });
                changerMobilePushViewModel.button = new ButtonViewModel(element.code);
                changerMobilePushViewModel.button = DashboardV7MapperUtils.extractButton(
                    element,
                    TmsCodes.care_hp_mobile,
                );
                model.changeMobile = changerMobilePushViewModel;
            }
            if (element.type === 'commercial') {
                this.setCommercialPush(element, commercialPushViewModel, contract);
            }
            if (element.type === 'alert') {
                dashModel.alerts = DashboardV7MapperUtils.getAlerts(pushes);
            }
            if (element.type === 'info') {
                dashModel.infos = DashboardV7MapperUtils.getInfos(pushes);
            }
            if (element.type === 'warning') {
                dashModel.warnings = DashboardV7MapperUtils.getWarnings(pushes);
            }
            if (element.type === 'who-uses-it') {
                dashModel.whoUsesIt = DashboardV7MapperUtils.getWhoUsesItInfo(element);
            }
        });
        model.commercialPush = commercialPushViewModel;
    }

    /**
     * Get tracking name for change-offer push based on element properties
     * @param {PushV7} element
     * @returns {string}
     * @private
     */
    private static getChangeOfferTrackingName(element: PushV7): string {
        if (element.idRefZone) {
            return element.idRefZone;
        }

        const elementTitle = element.title ? element.title : '';
        switch (elementTitle) {
            case 'Changer de forfait':
                return 'changer_de_forfait';
            case "Changer d'offre":
                return 'changer_d_offre';
            case 'Passer à la Fibre':
                return 'passer_a_la_fibre';
            case 'Votre logement est il éligible à la fibre ?':
                return 'logement_eligible_fibre';
            default:
                return this.formatTextTracking(element.title);
        }
    }

    private static setCommercialPush(
        element: PushV7,
        commercialPushViewModel: CommercialPushViewModel,
        contract?: ContractV7,
    ) {
        if (element.category === 'sim') {
            commercialPushViewModel.simPush = element;
            commercialPushViewModel.simPush.trackingData = Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: TmsCodes.care_hp_mobile,
                    trackingName: element.code,
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            });
        } else if (element.category === 'home') {
            commercialPushViewModel.homePush = element;
            commercialPushViewModel.homePush.trackingData = Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: TmsCodes.care_hp_maison,
                    trackingName: element.code,
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            });
        } else if (element.category === 'entertainment') {
            commercialPushViewModel.entertainmentPush = element;
            commercialPushViewModel.entertainmentPush.trackingData = Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.dashboard,
                    trackingArea: 'care_tvdivertissement',
                    trackingName: element.code,
                    additionalProperties: this.buildTrackingAdditionalProperties(contract),
                },
            });
        }
    }

    private static checkcontent(content: string[]): boolean {
        return content[content.length - 1].includes('Communications payantes');
    }

    private static formatContent(content: any): any {
        if (Array.isArray(content)) {
            content.forEach((item, index) => {
                if (index !== 0) {
                    content[index] = item.charAt(0).toLowerCase() + item.slice(1);
                    content[index] = ' ' + content[index];
                }
            });
        }
        return content;
    }

    private static formatTextTracking(trackName: string): string {
        return trackName.replace(/\s+/g, '_').toLowerCase();
    }

    /**
     * Build additional properties for tracking based on contract information
     * @param {ContractV7} contract - The contract data
     * @returns {Map<string, string>} Map of additional tracking properties
     * @private
     */
    private static buildTrackingAdditionalProperties(contract?: ContractV7): Map<string, string> {
        if (!contract) {
            return new Map<string, string>([
                ['contrat.offre_type', 'NR'],
                ['contrat.offre_label', 'NR'],
                ['contrat.offre_brand', 'NR'],
                ['contrat.role', 'NR'],
                ['contrat.hasCybersecureOption', 'false'],
            ]);
        }

        const hasCybersecure = contract.options?.some((option) => option.section === 'Cybersecure') || false;

        return new Map<string, string>([
            ['contrat.offre_type', contract.offer?.type || 'NR'],
            ['contrat.offre_label', contract.offer?.offerName || 'NR'],
            ['contrat.offre_brand', contract.brand || 'NR'],
            ['contrat.role', contract.identityType || 'NR'],
            ['contrat.hasCybersecureOption', hasCybersecure.toString()],
        ]);
    }
}
